import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import DropboxAccountDocument from "../../components/DropboxAccountDocument";
import Header from "components/Header";

import { formatBytes } from "../../utils/formateBytes";
import { getAccountDocumentFileApi } from "../../features/file/fileActions";

import {
  useGetAccountDocumentsFromAdminQuery,
  useUpdateAccountDocumentFromAdminMutation,
  useAddAccountDocumentFromAdminMutation,
  useDeleteAccountDocumentFromAdminMutation,
  useLazyGetAccountDocumentFilesFromAdminQuery,
  useDeleteAccountDocumentFilesFromAdminMutation,
} from "state/api";

import {
  Autocomplete,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  useTheme,
  TextField,
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Modal,
  Typography,
} from "@mui/material";

import { createFilterOptions } from "@mui/material/Autocomplete";
import {
  GridRowModes,
  DataGrid,
  GridToolbarContainer,
  GridActionsCellItem,
  useGridApiContext,
} from "@mui/x-data-grid";

import {
  Add as AddIcon,
  Save as SaveIcon,
  Close as CancelIcon,
  Edit as EditIcon,
  AttachFile as AttachFileIcon,
  DeleteOutlined as DeleteIcon,
  PreviewOutlined as PreviewOutlinedIcon,
} from "@mui/icons-material";

import { Alert as MuiAlert, Tooltip } from "@mui/material";

import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import { useTranslation } from "react-i18next";
import * as locales from "@mui/material/locale";
import { createTheme, ThemeProvider } from "@mui/material/styles";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function EditToolbar(props) {
  const { t, i18n } = useTranslation();
  const { setRows, setRowModesModel, rows } = props;

  const handleAddRecordClick = () => {
    //get the row id

    const newId =
      rows.length === 0
        ? 1
        : Math.max(...rows.map((r) => (r.id ? r.id : 0) * 1)) + 1;
    const id = newId;
    setRows((oldRows) => [
      ...oldRows,
      {
        id: newId,
        documentType: "",
        title: "",
        remark: "",
        numOfFiles: 0,
        isNew: true,
      },
    ]);
    setRowModesModel((oldModel) => ({
      ...oldModel,
      [id]: { mode: GridRowModes.Edit, fieldToFocus: "documentType" },
    }));
  };

  return (
    <GridToolbarContainer>
      <Button
        color="primary"
        startIcon={<AddIcon />}
        onClick={handleAddRecordClick}
      >
        {t("accountDocuments.Add Account Document")}
      </Button>
    </GridToolbarContainer>
  );
}

const AccountDocuments = () => {
  const { t, i18n } = useTranslation();
  const [locale, setLocale] = useState();
  const theme = useTheme();
  const themeWithLocale = useMemo(
    () => createTheme(theme, locales[locale]),
    [locale, theme]
  );

  const getCurrentLocale = () => {
    return t("currentLocale");
  };

  const currentLocale = getCurrentLocale();

  // set locale for datagrid
  useEffect(() => {
    if (currentLocale === "zh-Hant") {
      setLocale("zh");
    } else {
      setLocale("en");
    }
  }, [currentLocale]);
  const navigate = useNavigate();
  const location = useLocation();

  const { data, isLoading } = useGetAccountDocumentsFromAdminQuery();

  const [selectedAccountDocumentId, setSelectedAccountDocumentId] =
    useState(null);

  const [rows, setRows] = useState();
  const [rowModesModel, setRowModesModel] = useState({});

  const [previewUrl, setPreviewUrl] = useState("");
  const [showPreview, setShowPreview] = useState(false);

  const handleShowPreview = () => {
    setShowPreview(true);
  };

  const handleClosePreview = () => {
    setShowPreview(false);
  };

  const [openConfirmDeleteDialog, setOpenConfirmDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState("");

  const [openConfirmDeleteFileDialog, setOpenConfirmDeleteFileDialog] =
    useState(false);
  const [deleteFileId, setDeleteFileId] = useState("");

  //for snackbar
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  //for file modal
  const [openFileModal, setOpenFileModal] = React.useState(false);
  const handleOpenFileModal = () => setOpenFileModal(true);
  const handleCloseFileModal = () => setOpenFileModal(false);

  const [updateAccountDocument, { isLoading: isUpdating }] =
    useUpdateAccountDocumentFromAdminMutation();
  const [addAccountDocument, { isLoading: isAdding }] =
    useAddAccountDocumentFromAdminMutation();

  const [deleteAccountDocument, { isLoading: isDeleting }] =
    useDeleteAccountDocumentFromAdminMutation();
  const [deleteAccountDocumentFile, { isLoading: isDeletingFile }] =
    useDeleteAccountDocumentFilesFromAdminMutation();

  const [triggerFiles, { isLoading: isLoadingFiles, data: resultFiles }] =
    useLazyGetAccountDocumentFilesFromAdminQuery();

  // file modal
  const fileModalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 800,
    bgcolor: theme.palette.primary.main,
    border: "0px",
    boxShadow: 24,
    p: 4,
  };

  useEffect(() => {
    if (selectedAccountDocumentId) {
      //fetch
      triggerFiles({ id: selectedAccountDocumentId });
    }
  }, [selectedAccountDocumentId]);

  useEffect(() => {
    setRows(data);
  }, [isLoading, data]);

  // *** snackbar *** //
  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
  };

  const handlePreviewFileClick = (id) => async () => {
    try {
      let resp = {};
      resp = await getAccountDocumentFileApi(id, selectedAccountDocumentId);
      // get the file type
      const contentType = resp.headers["content-type"];
      // Check the file type and set the preview URL accordingly
      if (contentType.startsWith("image/")) {
        const imageUrl = URL.createObjectURL(new Blob([resp.data]));
        setPreviewUrl(imageUrl);
        handleShowPreview();
      } else {
        // For documents or videos, open a new tab for previewing/downloading
        window.open(
          URL.createObjectURL(new Blob([resp.data], { type: contentType }))
        );
      }
    } catch (error) {
      setSnackbarMessage("Error:" + error);
      // show snackbar
      setOpenSnackbar(true);
    }
  };

  // *** confirm delete dialog *** //
  const handleCloseConfirmDeleteDialog = () => {
    setDeleteFileId("");
    setOpenConfirmDeleteDialog(false);
  };

  // *** confirm delete File dialog *** //
  const handleDeleteFileClick = (id) => () => {
    // save the row id
    setDeleteFileId(id);
    setOpenConfirmDeleteFileDialog(true);
  };

  // *** confirm delete file dialog *** //
  const handleCloseConfirmDeleteFileDialog = () => {
    setDeleteFileId("");
    setOpenConfirmDeleteFileDialog(false);
  };

  // *** confirm delete file dialog *** //
  const handleSubmitConfirmDeleteFileDialog = async () => {
    try {
      let response = {};
      //updating
      response = await deleteAccountDocumentFile({
        id: selectedAccountDocumentId,
        body: { fileId: deleteFileId },
      }).unwrap();
      // get message
      if (response.message === "Delete Done!") {
        // set the snackbar message
        setSnackbarMessage(t("accountDocuments.snackbar.Record Deleted"));
        // show snackbar
        setOpenSnackbar(true);
      }
    } catch (error) {
      setSnackbarMessage("Error:" + error);
      // show snackbar
      setOpenSnackbar(true);
    }
    // //update the display
    // setRows(rows.filter((row) => row.id !== deleteId));
    //reset
    setDeleteFileId("");
    setOpenConfirmDeleteFileDialog(false);
  };

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleDeleteClick = (id) => () => {
    // save the row id
    setDeleteId(id);
    setOpenConfirmDeleteDialog(true);
  };

  const handleManageFilesClick = (id) => () => {
    let selectedRow = rows.find((row) => row.id === id);
    // update the selected account document id

    setSelectedAccountDocumentId(selectedRow["_id"]);
    //show attache file modal
    handleOpenFileModal();
  };

  const handleSubmitConfirmDeleteDialog = async () => {
    let selectedRow = rows.find((row) => row.id === deleteId);
    // // call api to delete the administrator
    try {
      let response = {};
      //updating
      response = await deleteAccountDocument({
        id: selectedRow["_id"], // actual id
      }).unwrap();
      // get message
      if (response.message === "Delete Done!") {
        // set the snackbar message
        setSnackbarMessage(t("accountDocuments.snackbar.Record Deleted"));
        // show snackbar
        setOpenSnackbar(true);
      }
    } catch (error) {
      // set the snackbar message
      setSnackbarMessage("Error: " + error);
      // show snackbar
      setOpenSnackbar(true);
    }
    //update the display
    setRows(rows.filter((row) => row.id !== deleteId));
    //reset
    setDeleteId("");
    setOpenConfirmDeleteDialog(false);
  };

  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };
  const handleRowEditStart = (params, event) => {
    event.defaultMuiPrevented = true;
  };
  const handleRowEditStop = (params, event) => {
    event.defaultMuiPrevented = true;
  };
  const handleProcessRowUpdateError = React.useCallback((error) => {
    // showSnackbar("error", error.message);
    //setSnackbarMessage("Error! - " + error.message);
  }, []);
  const processRowUpdate = async (newRow) => {
    try {
      let response = {};
      if (newRow["isNew"]) {
        //insert new record
        response = await addAccountDocument({
          data: newRow,
        }).unwrap();
        //get _id
        const new_id = response._id; //asset document _id
        newRow["_id"] = new_id;
      } else {
        //updating
        response = await updateAccountDocument({
          id: newRow["_id"],
          data: newRow,
        }).unwrap();
      }
      //get response message
      if (
        response.message === "Update Done!" ||
        response.message === "Add Done!" ||
        response.message === "Delete Done!"
      ) {
        // set the snackbar message
        if (response.message === "Update Done!")
          setSnackbarMessage(t("accountDocuments.snackbar.Record Updated"));
        else if (response.message === "Add Done!")
          setSnackbarMessage(t("accountDocuments.snackbar.Record Added"));
        else if (response.message === "Delete Done!")
          setSnackbarMessage(t("accountDocuments.snackbar.Record Deleted"));
        // show snackbar
        setOpenSnackbar(true);
        const updatedRow = { ...newRow, isNew: false };
        setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
        return updatedRow;
      }
    } catch (error) {
      return null;
    }
  };

  const renderListHeader = () => {
    let renderListHeader = (
      <Box>
        <h4>{t("accountDocuments.modal.List of current files")}</h4>
      </Box>
    );
    if (!isLoadingFiles && resultFiles) {
      renderListHeader = (
        <Box>
          <h4>
            {t("accountDocuments.modal.List of current files")} (
            {t("accountDocuments.modal.Total")} {resultFiles.length}{" "}
            {t("accountDocuments.modal.files")})
          </h4>
        </Box>
      );
    }
    return renderListHeader;
  };
  const renderFileList = () => {
    let renderListItem = <></>;
    if (!isLoadingFiles && resultFiles) {
      renderListItem = resultFiles.map((e, idx) => (
        <ListItem key={e._id}>
          <ListItemText
            primary={`${idx + 1}. ${e.originalname}`}
            secondary={`(File size: ${formatBytes(e.size)} bytes)`}
          />
          <ListItemIcon>
            <IconButton
              edge="end"
              aria-label={t("accountDocuments.modal.preview")}
              onClick={handlePreviewFileClick(e._id)}
            >
              <Tooltip title={t("accountDocuments.modal.preview")}>
                <PreviewOutlinedIcon />
              </Tooltip>
            </IconButton>
          </ListItemIcon>
          <ListItemIcon>
            <IconButton
              edge="end"
              aria-label={t("accountDocuments.modal.delete")}
              onClick={handleDeleteFileClick(e._id)}
            >
              <Tooltip title={t("accountDocuments.modal.delete")}>
                <DeleteIcon />
              </Tooltip>
            </IconButton>
          </ListItemIcon>
        </ListItem>
      ));
    }

    const content = (
      <List style={{ minWidth: 100, maxHeight: 200, overflow: "scroll" }}>
        {renderListItem}
      </List>
    );
    return content;
  };

  const filterOptions = createFilterOptions({
    limit: 500, // only show 500 entries, the rest need to be search
  });

  const columns = [
    { field: "id", headerName: "No", flex: 0.01, editable: false },
    {
      field: "documentType",
      headerName: t("accountDocuments.header.Document Type"),
      flex: 0.05,
      editable: true,
      type: "singleSelect",
      valueOptions: [
        {
          value: "Bank Statement",
          label: t("accountDocuments.select.Bank Statement"),
        },
        {
          value: "Commission Invoice",
          label: t("accountDocuments.select.Commission Invoice"),
        },
        {
          value: "TT Slip",
          label: t("accountDocuments.select.TT Slip"),
        },
        {
          value: "Appointment Document",
          label: t("accountDocuments.select.Appointment Document"),
        },
        {
          value: "Sale Document",
          label: t("accountDocuments.select.Sale Document"),
        },
        {
          value: "Scan Document",
          label: t("accountDocuments.select.Scan Document"),
        },
        {
          value: "Lease Management Document",
          label: t("accountDocuments.select.Lease Management Document"),
        },
      ],
    },
    {
      field: "title",
      headerName: t("accountDocuments.header.Title"),
      flex: 0.05,
      editable: true,
      type: "text",
    },
    {
      field: "remark",
      headerName: t("accountDocuments.header.Remark"),
      flex: 0.05,
      editable: true,
      type: "text",
    },
    {
      field: "numOfFiles",
      headerName: t("accountDocuments.header.Num of Files"),
      flex: 0.05,
      editable: false,
    },
    {
      field: "actions",
      type: "actions",
      headerName: t("accountDocuments.header.Actions"),
      width: 150,
      cellClassName: "actions",
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode && !isUpdating && !isAdding) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        if (isUpdating || isAdding) {
          return [
            <>
              <CircularProgress size="1.5rem" />
            </>,
          ];
        }

        return [
          <GridActionsCellItem
            icon={
              <Tooltip title={t("accountDocuments.menu.Edit Document Info")}>
                <EditIcon />
              </Tooltip>
            }
            label={t("accountDocuments.menu.Edit Account Document")}
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={
              <Tooltip title={t("accountDocuments.menu.Manage Files")}>
                <AttachFileIcon />
              </Tooltip>
            }
            label={t("accountDocuments.menu.Manage Files")}
            className="textPrimary"
            color="inherit"
            onClick={handleManageFilesClick(id)}
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label={t("accountDocuments.menu.Delete Document")}
            onClick={handleDeleteClick(id)}
            showInMenu
          />,
        ];
      },
    },
  ];

  const tableGrid = () => {
    let content;
    content = (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        {/** Confirm Dialog */}
        <Dialog
          open={openConfirmDeleteDialog}
          onClose={handleCloseConfirmDeleteDialog}
        >
          <DialogContent
            sx={{
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
            }}
          >
            <DialogContentText>
              {t("accountDocuments.dialog.Confirm Delete")}
            </DialogContentText>
          </DialogContent>
          <DialogActions
            sx={{
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
            }}
          >
            <Button
              onClick={handleCloseConfirmDeleteDialog}
              sx={{
                backgroundColor: theme.palette.background.alt,
                color: `${theme.palette.secondary[100]} !important`,
              }}
            >
              {t("accountDocuments.dialog.CANCEL")}
            </Button>
            <Button
              onClick={handleSubmitConfirmDeleteDialog}
              sx={{
                backgroundColor: theme.palette.background.alt,
                color: `${theme.palette.secondary[100]} !important`,
              }}
            >
              {t("accountDocuments.dialog.DELETE")}
            </Button>
          </DialogActions>
        </Dialog>
        {/** Snackbar */}
        <Snackbar
          open={openSnackbar}
          autoHideDuration={1000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            sx={{
              width: "100%",
              backgroundColor: theme.palette.secondary.light,
              color: theme.palette.background.alt,
              fontSize: "14px",
              fontWeight: "bold",
              padding: "10px 20px",
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
        <Dialog
          open={openConfirmDeleteFileDialog}
          onClose={handleCloseConfirmDeleteFileDialog}
        >
          <DialogContent
            sx={{
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
            }}
          >
            <DialogContentText>
              {t("accountDocuments.deleteFileDialog.Confirm Delete")}
            </DialogContentText>
          </DialogContent>
          <DialogActions
            sx={{
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
            }}
          >
            <Button
              onClick={handleCloseConfirmDeleteFileDialog}
              sx={{
                backgroundColor: theme.palette.background.alt,
                color: `${theme.palette.secondary[100]} !important`,
              }}
            >
              {t("accountDocuments.deleteFileDialog.CANCEL")}
            </Button>
            <Button
              onClick={handleSubmitConfirmDeleteFileDialog}
              sx={{
                backgroundColor: theme.palette.background.alt,
                color: `${theme.palette.secondary[100]} !important`,
              }}
            >
              {t("accountDocuments.deleteFileDialog.DELETE")}
            </Button>
          </DialogActions>
        </Dialog>
        <Modal
          open={openFileModal}
          onClose={handleCloseFileModal}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          style={{ overflow: "scroll" }}
        >
          <Box sx={fileModalStyle}>
            <Backdrop
              open={showPreview}
              onClick={handleClosePreview}
              sx={{ zIndex: 99, width: "100%" }}
            >
              <img src={previewUrl} width="200px" />
            </Backdrop>
            <Typography id="modal-modal-title" variant="h4" component="h2">
              {t("accountDocuments.modal.Manage Files")}
            </Typography>
            {renderListHeader()}
            <Box
              sx={{
                backgroundColor: theme.palette.primary[300],
                color: theme.palette.grey[1],
              }}
            >
              {renderFileList()}
            </Box>

            {isLoadingFiles && (
              <Box>
                <h4>Loading ...</h4>
              </Box>
            )}
            <Typography
              id="modal-modal-description"
              sx={{ mt: 2 }}
            ></Typography>

            <Divider
              variant="middle"
              sx={{
                backgroundColor: theme.palette.primary[200],
                color: theme.palette.grey[0],
              }}
            />
            <Box>
              <h4>{t("accountDocuments.modal.Add Files")}</h4>
            </Box>

            <DropboxAccountDocument
              accountDocumentId={selectedAccountDocumentId}
            />
          </Box>
        </Modal>
        <Box
          mt="40px"
          height="75vh"
          sx={{
            "& .MuiDataGrid-root": {
              border: "none",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "none",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
              borderBottom: "none",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: theme.palette.primary.light,
            },
            "& .MuiDataGrid-footerContainer": {
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
              borderTop: "none",
            },
            "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
              color: `${theme.palette.secondary[200]} !important`,
            },
          }}
        >
          <ThemeProvider theme={themeWithLocale}>
            <DataGrid
              loading={isLoading || !data}
              rows={rows || []}
              columns={columns}
              getRowId={(row) => row.id}
              editMode="row"
              rowModesModel={rowModesModel}
              onRowModesModelChange={handleRowModesModelChange}
              onRowEditStart={handleRowEditStart}
              onRowEditStop={handleRowEditStop}
              processRowUpdate={processRowUpdate}
              onProcessRowUpdateError={handleProcessRowUpdateError}
              slots={{
                toolbar: EditToolbar,
              }}
              slotProps={{
                toolbar: { setRows, setRowModesModel, rows },
              }}
            />
          </ThemeProvider>
        </Box>
      </LocalizationProvider>
    );
    return content;
  };

  return (
    <Box m="1.5rem 2.5rem">
      <Header
        title={t("accountDocuments.ACCOUNT DOCUMENTS")}
        subtitle={t("accountDocuments.List of Account Documents")}
      />

      {tableGrid()}
    </Box>
  );
};

export default AccountDocuments;
